import React from "react";
import { Accordion, Table, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faEnvelope, faUser } from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import HomeNameBadge from "../../../Partials/HomeNameBadge/index.jsx";

function roleBadgeVariant(role) {
    if (role === 'owner') return 'success';
    if (role === 'dealer') return 'warning';
    return 'secondary';
}

function HomeCard({ home = null, isLoading = false, error = null }) {
    const { formatDate } = useTimezone();

    if (isLoading && !home) {
        return (
            <div className="sidebarAccordion mb-2">
                <div className="text-center p-3">
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </div>
        );
    }
    if (error) {
        return <Alert variant="danger" className="mb-2">Error loading home: {error}</Alert>;
    }
    if (!home) return null;

    const members = home.members || [];
    const invites = home.invites || [];

    return (
        <Accordion defaultActiveKey={["Home"]} className="sidebarAccordion mb-2" alwaysOpen>
            <Accordion.Item eventKey="Home">
                <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-2">
                        <div><HomeNameBadge name={home.name} style={{ fontSize: '0.85rem' }} /></div>
                        <Badge bg="dark">{home.home_code}</Badge>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="p-2">
                    <div className="p-2">
                        <FloatingLabel label="Home Name" className="mb-2">
                            <Form.Control readOnly value={home.name || ''} />
                        </FloatingLabel>
                        <div className="d-flex gap-2 mb-2">
                            <FloatingLabel label="Home Code" className="flex-fill">
                                <Form.Control readOnly value={home.home_code || ''} />
                            </FloatingLabel>
                            <FloatingLabel label="Created" className="flex-fill">
                                <Form.Control readOnly value={home.created ? formatDate(home.created) : ''} />
                            </FloatingLabel>
                        </div>
                        {home.address && (
                            <FloatingLabel label="Address" className="mb-0">
                                <Form.Control readOnly value={home.address} />
                            </FloatingLabel>
                        )}
                    </div>

                    <Accordion defaultActiveKey="0" className="mb-2">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <div><FontAwesomeIcon icon={faUsers} className="me-2" />Members</div>
                                    <Badge bg="dark">{members.length}</Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-2">
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr><th>Email</th><th>Name</th><th>Role</th></tr>
                                    </thead>
                                    <tbody>
                                        {members.map((m) => (
                                            <tr key={m.user_id}>
                                                <td>
                                                    {m.email ? (
                                                        <a
                                                            href={`#/u/${encodeURIComponent(m.user_id)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Open this user's dashboard in a new tab"
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            <Badge className="integrationBadge" style={{ '--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a' }}>
                                                                <FontAwesomeIcon icon={faUser} className="me-1" size="xs" />{m.email}
                                                            </Badge>
                                                        </a>
                                                    ) : '—'}
                                                </td>
                                                <td>
                                                    {m.name || '—'}
                                                    {m.business_contact?.company_name && (
                                                        <span className="text-muted"> ({m.business_contact.company_name})</span>
                                                    )}
                                                </td>
                                                <td><Badge bg={roleBadgeVariant(m.role)}>{m.role}</Badge></td>
                                            </tr>
                                        ))}
                                        {members.length === 0 && (
                                            <tr><td colSpan={3} className="text-center text-muted">No members</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>

                    <Accordion defaultActiveKey={invites.length > 0 ? "0" : null} className="mb-0">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <div><FontAwesomeIcon icon={faEnvelope} className="me-2" />Pending Invites</div>
                                    <Badge bg="dark">{invites.length}</Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-2">
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr><th>Identifier</th><th>Role</th><th>Status</th><th>Expires</th></tr>
                                    </thead>
                                    <tbody>
                                        {invites.map((inv) => (
                                            <tr key={inv.id}>
                                                <td>{inv.identifier || '—'}</td>
                                                <td>
                                                    <Badge bg={roleBadgeVariant(inv.role)}>{inv.role || '—'}</Badge>
                                                    {inv.role_after_transfer && (
                                                        <span className="text-muted"> → {inv.role_after_transfer}</span>
                                                    )}
                                                </td>
                                                <td><Badge bg="secondary">{inv.status || '—'}</Badge></td>
                                                <td>{inv.expires_at ? formatDate(inv.expires_at) : '—'}</td>
                                            </tr>
                                        ))}
                                        {invites.length === 0 && (
                                            <tr><td colSpan={4} className="text-center text-muted">No pending invites</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default HomeCard;
